import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { ROUTES } from './config/routes';
import { UserRole } from './types';
import MainLayout from './components/common/Layout/MainLayout';
import AuthGuard from './components/common/Guards/AuthGuard';
import RoleGuard from './components/common/Guards/RoleGuard';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import ApplicationList from './pages/Applications/ApplicationList';
import ApplicationDetail from './pages/Applications/ApplicationDetail';
import ApplicationForm from './pages/Applications/ApplicationForm';
import ApplicationUsers from './pages/Applications/ApplicationUsers';
import GlobalSegmentsListPage from './pages/global-segments/GlobalSegmentsListPage';
import GlobalSegmentForm from './pages/global-segments/GlobalSegmentForm';
import GlobalCampaignsListPage from './pages/global-campaigns/GlobalCampaignsListPage';
import GlobalCampaignForm from './pages/global-campaigns/GlobalCampaignForm';
import PushManagementPage from './pages/PushManagement/PushManagementPage';
import SendPush from './pages/Push/SendPush';
import PushDetail from './pages/Push/PushDetail';
import TemplateList from './pages/Templates/TemplateList';
import TemplateForm from './pages/Templates/TemplateForm';
import CampaignForm from './pages/Campaigns/CampaignForm';
import EventTriggerForm from './pages/EventTriggers/EventTriggerForm';
import RecurringScheduleForm from './pages/RecurringSchedules/RecurringScheduleForm';
import SegmentForm from './pages/Segments/SegmentForm';
import Statistics from './pages/Statistics/Statistics';
import { UserList } from './pages/Users/UserList';
import { UserForm } from './pages/Users/UserForm';
import NotFound from './pages/NotFound/NotFound';
import ABTestForm from './components/push/ABTestForm';
import ABTestResults from './components/push/ABTestResults';
import PushGroupDetail from './pages/Push/PushGroupDetail';
import BrandLoader from './components/common/Loaders/BrandLoader';
import { useMinimumLoading } from './hooks/useMinimumLoading';

const RootRedirect: React.FC = () => {
  const { userRole } = useAuth();
  if (
    userRole === UserRole.ACC_MANAGER ||
    userRole === UserRole.EXECUTOR ||
    userRole === UserRole.VIEWER ||
    userRole === UserRole.BUYER
  ) {
    return <Navigate to="/push-center" replace />;
  }
  return <Dashboard />;
};

const App: React.FC = () => {
  const { isAuthenticated, isLoading, refreshUser } = useAuth();
  const showLoader = useMinimumLoading(isLoading, 2000);

  useEffect(() => {
    if (isAuthenticated) {
      refreshUser();
    }
  }, [isAuthenticated, refreshUser]);

  if (showLoader) {
    return <BrandLoader />;
  }

  return (
    <div className="app">
      <Routes>
        <Route path={ROUTES.LOGIN} element={<Login />} />
        <Route
          path="/"
          element={
            <AuthGuard>
              <MainLayout />
            </AuthGuard>
          }
        >
          <Route index element={<RootRedirect />} />
          <Route path="applications">
            <Route
              index
              element={
                <RoleGuard roles={[UserRole.ADMIN]}>
                  <ApplicationList />
                </RoleGuard>
              }
            />
            <Route
              path="create"
              element={
                <RoleGuard roles={[UserRole.ADMIN]}>
                  <ApplicationForm />
                </RoleGuard>
              }
            />
            <Route
              path=":id"
              element={
                <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                  <ApplicationDetail />
                </RoleGuard>
              }
            />
            <Route
              path=":id/edit"
              element={
                <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                  <ApplicationForm />
                </RoleGuard>
              }
            />
            <Route
              path=":id/users"
              element={
                <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                  <ApplicationUsers />
                </RoleGuard>
              }
            />
          </Route>
          <Route path="push-center">
            <Route
              index
              element={
                <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR, UserRole.VIEWER, UserRole.ACC_MANAGER, UserRole.BUYER]}>
                  <PushManagementPage />
                </RoleGuard>
              }
            />
            <Route
              path="send"
              element={
                <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                  <SendPush />
                </RoleGuard>
              }
            />
            <Route
              path="history/:messageId"
              element={
                <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR, UserRole.ACC_MANAGER, UserRole.VIEWER, UserRole.BUYER]}>
                  <PushDetail />
                </RoleGuard>
              }
            />
            <Route
              path="history/group/:groupKey"
              element={
                <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR, UserRole.ACC_MANAGER, UserRole.VIEWER, UserRole.BUYER]}>
                  <PushGroupDetail />
                </RoleGuard>
              }
            />
            <Route path="ab-tests">
              <Route
                path="create"
                element={
                  <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                    <ABTestForm />
                  </RoleGuard>
                }
              />
              <Route
                path=":id"
                element={
                  <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                    <ABTestResults />
                  </RoleGuard>
                }
              />
            </Route>
            <Route path="campaigns">
              <Route
                path="create"
                element={
                  <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                    <CampaignForm />
                  </RoleGuard>
                }
              />
              <Route
                path=":id/edit"
                element={
                  <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                    <CampaignForm />
                  </RoleGuard>
                }
              />
            </Route>
            <Route path="triggers">
              <Route
                path="create"
                element={
                  <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                    <EventTriggerForm />
                  </RoleGuard>
                }
              />
              <Route
                path=":id/edit"
                element={
                  <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                    <EventTriggerForm />
                  </RoleGuard>
                }
              />
            </Route>
            <Route path="schedules">
              <Route
                path="create"
                element={
                  <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                    <RecurringScheduleForm />
                  </RoleGuard>
                }
              />
              <Route
                path=":id/edit"
                element={
                  <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                    <RecurringScheduleForm />
                  </RoleGuard>
                }
              />
            </Route>
            <Route path="segments">
              <Route
                path="create"
                element={
                  <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                    <SegmentForm />
                  </RoleGuard>
                }
              />
              <Route
                path=":id/edit"
                element={
                  <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                    <SegmentForm />
                  </RoleGuard>
                }
              />
            </Route>
          </Route>
          <Route path="push" element={<Navigate to="/push-center" replace />} />
          <Route path="campaigns" element={<Navigate to="/push-center" replace />} />
          <Route path="event-triggers" element={<Navigate to="/push-center" replace />} />
          <Route path="recurring-schedules" element={<Navigate to="/push-center" replace />} />
          <Route path="segments" element={<Navigate to="/push-center" replace />} />
          <Route path="global">
            <Route path="segments">
              <Route
                index
                element={
                  <RoleGuard roles={[UserRole.ADMIN]}>
                    <GlobalSegmentsListPage />
                  </RoleGuard>
                }
              />
              <Route
                path="create"
                element={
                  <RoleGuard roles={[UserRole.ADMIN]}>
                    <GlobalSegmentForm />
                  </RoleGuard>
                }
              />
              <Route
                path=":id/edit"
                element={
                  <RoleGuard roles={[UserRole.ADMIN]}>
                    <GlobalSegmentForm />
                  </RoleGuard>
                }
              />
            </Route>
            <Route path="campaigns">
              <Route
                index
                element={
                  <RoleGuard roles={[UserRole.ADMIN]}>
                    <GlobalCampaignsListPage />
                  </RoleGuard>
                }
              />
              <Route
                path="create"
                element={
                  <RoleGuard roles={[UserRole.ADMIN]}>
                    <GlobalCampaignForm />
                  </RoleGuard>
                }
              />
              <Route
                path=":id/edit"
                element={
                  <RoleGuard roles={[UserRole.ADMIN]}>
                    <GlobalCampaignForm />
                  </RoleGuard>
                }
              />
            </Route>
          </Route>
          <Route path="templates">
            <Route
              index
              element={
                <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                  <TemplateList />
                </RoleGuard>
              }
            />
            <Route
              path="create"
              element={
                <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                  <TemplateForm />
                </RoleGuard>
              }
            />
            <Route
              path=":id/edit"
              element={
                <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                  <TemplateForm />
                </RoleGuard>
              }
            />
          </Route>
          <Route
            path="statistics"
            element={
              <RoleGuard roles={[UserRole.ADMIN, UserRole.EXECUTOR]}>
                <Statistics />
              </RoleGuard>
            }
          />
          <Route
            path="users"
            element={
              <RoleGuard roles={[UserRole.ADMIN]}>
                <UserList />
              </RoleGuard>
            }
          />
          <Route
            path="users/create"
            element={
              <RoleGuard roles={[UserRole.ADMIN]}>
                <UserForm />
              </RoleGuard>
            }
          />
          <Route
            path="users/:id/edit"
            element={
              <RoleGuard roles={[UserRole.ADMIN]}>
                <UserForm />
              </RoleGuard>
            }
          />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

export default App;